import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { TradeType } from "@/interfaces/trade";
import type { Advertisement } from "@/interfaces/advertisement";
import type { P2PMarketFilters } from "@/components/p2p/filter/interface";

interface P2PMarketState extends P2PMarketFilters {
  side: TradeType;

  amount: string;
  searchQuery: string;
  paymentMethodFilter: string;

  currentPage: number;

  selectedOfferId: string | null;
  selectedAd: Advertisement | null;
}

const initialFilters: P2PMarketFilters = {
  cryptoCode: null,
  fiatCurrencyId: null,

  minAmount: "",
  maxAmount: "",

  paymentMethodIds: [],

  verifiedOnly: false,
  trustedOnly: false,

  minCompletionRate: null,

  maxReleaseTime: null,

  sortBy: "BEST_MATCH",
};

const initialState: P2PMarketState = {
  ...initialFilters,

  side: TradeType.BUY,

  amount: "",
  searchQuery: "",
  paymentMethodFilter: "",

  currentPage: 1,

  selectedOfferId: null,
  selectedAd: null,
};

const p2pMarketSlice = createSlice({
  name: "p2pMarket",

  initialState,

  reducers: {
    setSide(state, action: PayloadAction<TradeType>) {
      state.side = action.payload;
      state.currentPage = 1;
    },

    setCryptoCode(state, action: PayloadAction<string | null>) {
      state.cryptoCode = action.payload;
      state.currentPage = 1;
    },

    setFiatCurrencyId(state, action: PayloadAction<string | null>) {
      state.fiatCurrencyId = action.payload;

      /*
       * Payment methods belong to a fiat currency.
       * Changing fiat invalidates previously selected payment methods.
       */
      state.paymentMethodIds = [];

      state.paymentMethodFilter = "";

      state.currentPage = 1;
    },

    setAmount(state, action: PayloadAction<string>) {
      state.amount = action.payload;
      state.currentPage = 1;
    },

    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
      state.currentPage = 1;
    },

    setPaymentMethodFilter(state, action: PayloadAction<string>) {
      state.paymentMethodFilter = action.payload;
      state.currentPage = 1;
    },

    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },

    setMarketFilters(state, action: PayloadAction<Partial<P2PMarketFilters>>) {
      Object.assign(state, action.payload);

      /*
       * If the advanced payment method filter is being used,
       * keep the simple payment filter in sync.
       */
      if (action.payload.paymentMethodIds !== undefined) {
        state.paymentMethodFilter =
          action.payload.paymentMethodIds.length === 1
            ? action.payload.paymentMethodIds[0]
            : "";
      }

      state.currentPage = 1;
    },

    setSelectedOfferId(state, action: PayloadAction<string | null>) {
      state.selectedOfferId = action.payload;
    },

    setSelectedAd(state, action: PayloadAction<Advertisement | null>) {
      state.selectedAd = action.payload;
    },

    resetFilters(state) {
      state.cryptoCode = null;
      state.fiatCurrencyId = null;

      state.minAmount = "";
      state.maxAmount = "";

      state.paymentMethodIds = [];

      state.verifiedOnly = false;
      state.trustedOnly = false;

      state.minCompletionRate = null;
      state.maxReleaseTime = null;

      state.sortBy = "BEST_MATCH";

      state.amount = "";
      state.searchQuery = "";
      state.paymentMethodFilter = "";

      state.currentPage = 1;
    },

    resetMarketState() {
      return initialState;
    },
  },
});

export const {
  setSide,
  setCryptoCode,
  setFiatCurrencyId,
  setAmount,
  setSearchQuery,
  setPaymentMethodFilter,
  setCurrentPage,
  setMarketFilters,
  setSelectedOfferId,
  setSelectedAd,
  resetFilters,
  resetMarketState,
} = p2pMarketSlice.actions;

export default p2pMarketSlice.reducer;
